import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import withNavigate from "../withNavigate";
import { Box, Drawer, Button } from "@mui/material";
import { modal } from "../../../js/modules/Dialogs";
import CreateCampaign from "./CreateCampaign";
import Pagination from "../../../components/commonUI/Pagination/Pagination";
import DataTable from "../../../components/commonUI/DataTable/DataTable";
import { EditIcon } from "../../../components/commonUI/Icons/SvgIcon";
import { CAMPAIGN_COLUMNS ,MOCK_CAMPAIGNS} from "../../../config/constants";

const CampaignsTab = ({ userDetails, navigate }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [filter, setFilter] = useState("all");
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [selectedCheckbox, setSelectedCheckbox] = useState([]);
  const [sort, setSort] = useState("");
  const [campaigns, setCampaigns] = useState(MOCK_CAMPAIGNS);

  const toggleDrawer = useCallback(
    (open, view = null, campaign = null) => {
      setDrawerOpen(open);
      if (open) {
        setCurrentView(view);
        setSelectedCampaign(campaign || null);
      }
    },
    []
  );

  const SORT_FIELDS = useMemo(() => ({
    name: { asc: "name=1", desc: "name=-1" },
    discountCodes: { asc: "discountCodes=1", desc: "discountCodes=-1" },
    activeCodes: { asc: "activeCodes=1", desc: "activeCodes=-1" },
    totalUses: { asc: "totalUses=1", desc: "totalUses=-1" },
    created: { asc: "created=1", desc: "created=-1" },
  }), []);

  const columns = useMemo(() => CAMPAIGN_COLUMNS, []);

  const sortCampaigns = useCallback((event, field) => {
    if (event) event.preventDefault();
    const currentSort = SORT_FIELDS[field];
    if (!currentSort) return;
    const newSort = sort === currentSort.asc ? currentSort.desc : currentSort.asc;
    setSort(newSort);
    setPage(1);
  }, [sort, SORT_FIELDS]);

  const handleCampaignCheckbox = useCallback((campaignId) => {
    setSelectedCheckbox((prev) =>
      prev.includes(campaignId)
        ? prev.filter((item) => item !== campaignId)
        : [...prev, campaignId]
    );
  }, []);

  const handleEditClick = useCallback((campaign) => {
    toggleDrawer(true, "edit", campaign);
  }, [toggleDrawer]);

  const renderCampaigns = useCallback((campaign, index) => (
    <tr key={index}>
      <td>
        <strong>{campaign.name}</strong>
      </td>
      <td>{campaign.audience}</td>
      <td className="text-center">{campaign.discountCodes}</td>
      <td className="text-center">{campaign.activeCodes}</td>
      <td className="text-center">{campaign.totalUses}</td>
      <td>{campaign.created}</td>
      <td>
        <Button
          className="btn btn-sm"
          onClick={() => handleEditClick(campaign)}
          sx={{
            backgroundColor: "#000",
            color: "#fff",
            padding: "6px 16px",
            fontSize: "14px",
            fontWeight: "500",
            border: "none",
            "&:hover": { backgroundColor: "#1a1a1a" },
          }}
        >
          Edit
        </Button>
      </td>
    </tr>
  ), [handleEditClick]);

  const handleFilterChange = useCallback((filterValue) => {
    setFilter(filterValue);
    setPage(1);
  }, []);

  const getSortClass = useCallback((key) => {
    if (sort === `${key}=1`) return "sorting-icon sorting-icon-up";
    if (sort === `${key}=-1`) return "sorting-icon sorting-icon-down";
    return "";
  }, [sort]);

  const handleSelectAll = useCallback((e) => {
    setSelectedCheckbox(
      e.target.checked ? campaigns.map((campaign) => campaign._id) : []
    );
  }, [campaigns]);

  const renderHeader = useCallback(() => (
    <>
      {columns.map((column) => (
        <th key={column.key} className={column.key === "discountCodes" || column.key === "activeCodes" || column.key === "totalUses" ? "text-center" : ""}>
          {column.sortable ? (
            <button
              className={getSortClass(column.sortKey)}
              onClick={(e) => sortCampaigns(e, column.sortKey)}
            >
              {column.label}
            </button>
          ) : (
            column.label
          )}
        </th>
      ))}
    </>
  ), [columns, getSortClass, sortCampaigns]);

  return (
    <div className="mb-24">
      <DataTable
        data={campaigns}
        columns={columns}
        renderRow={renderCampaigns}
        onSort={sortCampaigns}
        getSortClass={getSortClass}
        renderHeader={renderHeader}
        emptyMessage="No Campaigns found"
        tableClassName="table clientTable"
        colSpan={7}
      />

      <Pagination
        pageSize={pageSize}
        totalPages={totalPages}
        page={page}
        setPage={setPage}
        inputValue={page}
        setInputValue={setPage}
        setPageSize={setPageSize}
      />

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => toggleDrawer(false)}
        ModalProps={{
          BackdropProps: {
            style: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
          },
        }}
        sx={{
          boxShadow: "none",
          "& .MuiDrawer-paper": { boxShadow: "none" },
        }}
      >
        <Box
          sx={{ width: 600 }}
          role="presentation"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <CreateCampaign
            view={currentView}
            campaign={selectedCampaign}
            onClose={() => toggleDrawer(false)}
          />
        </Box>
      </Drawer>
    </div>
  );
};

CampaignsTab.propTypes = {
  userDetails: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withNavigate(CampaignsTab);
